<script lang="ts">
	import { getContext } from "svelte";
	import { twMerge } from "tailwind-merge";
	import { ConversionType } from "#gen/bundles/v1/bundle_pb.js";
	import { Mode } from "#gen/bundles/v1/service_pb.js";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import TokenizedText from "#lib/components/TokenizedText.svelte";
	import { HORIZONTAL_JUSTIFY, IMAGE_BUTTON_DEFAULT, IMAGE_BUTTON_TEXT_LAYOUT, VERTICAL_ALIGN_ITEMS } from "#lib/components/flow/editor/appearance/constants.js";
	import { matchAspectRatioPreset } from "#lib/components/images/utils.js";
	import type { ComponentProps } from "#lib/components/page/ComponentProps.js";
	import { booleanValue, numberValue, stringValue, structValue } from "#lib/struct.js";
	import type { TokenProvider } from "#lib/tokenProvider.js";
	import type { Writable } from "svelte/store";

	let { id, style, page, mode, connectable = false, connectionRequired = false }: ComponentProps = $props();

	const tokenProvider: Writable<TokenProvider> = getContext("tokenProvider");
	let invalid = $state(false);

	const config = $derived(structValue($page.config, id));
	const execute: (conversionType: ConversionType, conversionComponent: string, sourceAnchor?: string) => Promise<boolean> = getContext("execute");

	const hidden = $derived(booleanValue(config, "hidden"));
	const disabled = $derived(booleanValue(config, "disabled"));
	const caption = $derived(stringValue(config, "caption"));
	const subheader = $derived(stringValue(config, "subheader"));
	const isEditing = $derived(mode && mode === Mode.EDITING);
	const showTokenizedPreview = $derived(isEditing && $tokenProvider?.hasSubstitutionToken(caption));
	const showTokenizedSubheader = $derived(isEditing && $tokenProvider?.hasSubstitutionToken(subheader));
	const src = $derived(stringValue(config, "src"));
	const frameAspectRatio = $derived(matchAspectRatioPreset(numberValue(config, "aspectRatio")) ?? (!src ? IMAGE_BUTTON_DEFAULT.aspectRatio : undefined));

	const borderRadius = $derived(stringValue(config, "borderRadius") ?? IMAGE_BUTTON_DEFAULT.borderRadius);
	const backgroundColor = $derived(stringValue(config, "backgroundColor") ?? IMAGE_BUTTON_DEFAULT.backgroundColor);
	const overlayColor = $derived(stringValue(config, "overlayColor") ?? IMAGE_BUTTON_DEFAULT.overlayColor);
	const borderColor = $derived(stringValue(config, "borderColor") ?? IMAGE_BUTTON_DEFAULT.borderColor);
	const textColor = $derived(stringValue(config, "textColor") ?? IMAGE_BUTTON_DEFAULT.textColor);
	const textAlignment = $derived(stringValue(config, "textAlignment") ?? IMAGE_BUTTON_DEFAULT.textAlignment);
	const verticalAlignment = $derived(stringValue(config, "verticalAlignment") ?? IMAGE_BUTTON_DEFAULT.verticalAlignment);
	const textBoxWidth = $derived(numberValue(config, "textBoxWidth") ?? IMAGE_BUTTON_DEFAULT.textBoxWidth);
	const textShouldOverlay = $derived(booleanValue(config, "textShouldOverlay") ?? IMAGE_BUTTON_DEFAULT.textShouldOverlay);
	const textSize = $derived(stringValue(config, "textSize") ?? IMAGE_BUTTON_DEFAULT.textSize);
	const textLayout = $derived(IMAGE_BUTTON_TEXT_LAYOUT[textSize] ?? IMAGE_BUTTON_TEXT_LAYOUT[IMAGE_BUTTON_DEFAULT.textSize]);
	const showHeader = $derived(Boolean(caption) || showTokenizedPreview);
	const showSubheader = $derived(Boolean(subheader) || showTokenizedSubheader);
	const showText = $derived(showHeader || showSubheader);
	const overlayAlignItems = $derived(VERTICAL_ALIGN_ITEMS[verticalAlignment] ?? VERTICAL_ALIGN_ITEMS[IMAGE_BUTTON_DEFAULT.verticalAlignment]);
	const overlayJustifyContent = $derived(HORIZONTAL_JUSTIFY[textAlignment] ?? HORIZONTAL_JUSTIFY[IMAGE_BUTTON_DEFAULT.textAlignment]);
	const textAlignItems = $derived(HORIZONTAL_JUSTIFY[textAlignment] ?? HORIZONTAL_JUSTIFY[IMAGE_BUTTON_DEFAULT.textAlignment]);
	const editingClass = $derived(isEditing ? "pointer-events-none cursor-default" : "");
	const imageClass = $derived(frameAspectRatio ? "block h-full w-full object-cover" : "block h-auto w-full");

	async function handleClick(e: MouseEvent) {
		e.preventDefault();
		e.stopPropagation();
		try {
			await execute(ConversionType.NONE, id, id);
		} catch (_: unknown) {
			// TODO: Use the error here to show a helpful message to the user
		}
	}

	async function handleKeyDown(e: KeyboardEvent) {
		if (e.key === "Enter") {
			e.preventDefault();
			e.stopPropagation();
			await execute(ConversionType.NONE, id, id);
		}
	}
</script>

{#snippet cardHeader()}
	{#if showHeader}
		<div class="w-fit max-w-full min-w-0" style={textShouldOverlay ? `background-color: ${backgroundColor};` : undefined}>
			<P
				class="line-clamp-2 wrap-break-word"
				style={`
					color: ${textColor};
					font-size: ${textLayout.headerSize};
					font-weight: bold;
					text-align: ${textAlignment};
					overflow-wrap: anywhere;
				`}
			>
				{#if showTokenizedPreview}
					<TokenizedText provider={$tokenProvider} text={caption} {mode} />
				{:else}
					{caption}
				{/if}
			</P>
		</div>
	{/if}
{/snippet}

{#snippet cardSubheader()}
	{#if showSubheader}
		<div class="w-fit max-w-full min-w-0" style={textShouldOverlay ? `background-color: ${backgroundColor};` : undefined}>
			<P
				class="line-clamp-2 wrap-break-word"
				style={`
					color: ${textColor};
					font-size: ${textLayout.subheaderSize};
					font-weight: normal;
					text-align: ${textAlignment};
					overflow-wrap: anywhere;
				`}
			>
				{#if showTokenizedSubheader}
					<TokenizedText provider={$tokenProvider} text={subheader} {mode} />
				{:else}
					{subheader}
				{/if}
			</P>
		</div>
	{/if}
{/snippet}

<div class="relative w-full leading-none" class:hidden={mode !== Mode.EDITING && hidden}>
	{#if mode === Mode.EDITING && hidden}
		<div class="absolute -top-1 -right-1 z-99 flex h-7 w-7 items-center justify-center rounded-full border bg-white/90 text-gray-900">
			<Icon iconSet="heroicons" name="eye-slash" class=" h-4 w-4" />
		</div>
	{/if}
	<button
		type="button"
		data-flow-card
		data-linkable-id={connectable ? id : undefined}
		data-connection-required={connectable ? String(connectionRequired) : undefined}
		class={twMerge("block w-full overflow-hidden p-0", stringValue(config, "class") ?? "", editingClass)}
		style={`
			${style ?? ""}
			border-radius: ${borderRadius};
			background-color: ${textShouldOverlay ? "transparent" : backgroundColor};
			border: 1px solid ${borderColor};
			height: ${textShouldOverlay ? "auto" : "100%"};
			background-clip: padding-box;
			opacity: ${disabled ? 0.5 : 1};
		`}
		onclick={mode === Mode.EDITING ? undefined : handleClick}
		onkeydown={mode === Mode.EDITING ? undefined : handleKeyDown}
		{disabled}
		aria-label={[caption, subheader].filter(Boolean).join(" ") || "Card"}
		tabindex="0"
	>
		<div class="relative w-full" style={frameAspectRatio ? `aspect-ratio: ${frameAspectRatio}` : undefined}>
			{#if src}
				{#if invalid}
					<img src="/carousel-1.svg" alt="Placeholder" class={imageClass} />
				{:else}
					<img {src} alt={stringValue(config, "alt") || "Image"} class={imageClass} onerror={() => (invalid = true)} />
				{/if}
			{:else}
				<div class="flex h-full w-full items-center justify-center bg-white">
					<Icon class="h-10 w-10 text-gray-300" iconSet="heroicons" name="photo" />
				</div>
			{/if}
			{#if textShouldOverlay}
				<div class="absolute inset-0" style="background-color: {overlayColor};"></div>
				{#if showText}
					<div class="absolute inset-0 flex" style="align-items: {overlayAlignItems}; justify-content: {overlayJustifyContent}; padding: {textLayout.paddingX};">
						<div class="flex min-w-0 flex-col" style="max-width: {textBoxWidth}%; gap: {showHeader && showSubheader ? textLayout.gap : 0}; align-items: {textAlignItems};">
							{@render cardHeader()}
							{@render cardSubheader()}
						</div>
					</div>
				{/if}
			{/if}
		</div>
		{#if !textShouldOverlay}
			<div class="flex min-h-8 min-w-0 flex-col" style="border-top: 1px solid {borderColor}; padding: {textLayout.padding}; gap: {showHeader && showSubheader ? textLayout.gap : 0}; align-items: {textAlignItems};">
				{@render cardHeader()}
				{@render cardSubheader()}
			</div>
		{/if}
	</button>
</div>
